'use client'

import * as React from 'react'
import {isEditing} from '@builder.io/sdk-react'
import {Drawer as DrawerPrimitive} from 'vaul'
import DrawerCloseIcon from 'hec/assets/icons/drawer-close-icon'
import {ScrollArea} from './scroll-area'

import {cn} from '@/lib/utils'

const Drawer = ({
    shouldScaleBackground = true,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {shouldScaleBackground?: boolean}) => (
    <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        handleOnly={true}
        {...props}
    />
)
Drawer.displayName = 'Drawer'

const closeDrawerEvent = new Event('closeDrawer')

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <DrawerPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed top-0 left-0 w-full h-full bg-[rgba(15,31,54,0.3)] data-[state=open]:animate-overlay-show data-[state=closed]:animate-overlay-hide z-50',
            className
        )}
        data-testid={dataTestId || 'drawer-DrawerOverlay'}
        {...props}
    />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, children, 'data-testid': dataTestId, ...props}, ref) => {
    // On mobile, the virtual keyboard resizes the viewport but fixed containers
    // with custom scroll (ScrollArea) don't auto-scroll to the focused input.
    // Listen for visualViewport resize and scroll the active element into view.
    React.useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null
        if (!vv) return
        const onResize = () => {
            const el = document.activeElement
            if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
                el.scrollIntoView({block: 'center', behavior: 'smooth'})
            }
        }
        vv.addEventListener('resize', onResize)
        return () => vv.removeEventListener('resize', onResize)
    }, [])

    return (
        <DrawerPortal data-testid={dataTestId || 'drawer-DrawerPortal'}>
            <DrawerOverlay data-testid={dataTestId || 'drawer-DrawerOverlay'} />
            <DrawerPrimitive.Content
                ref={ref}
                className={cn(
                    'left-0 bottom-0 z-50 w-full overflow-y-auto sm:max-w-[25rem] bg-white data-[state=open]:animate-drawer-show-mobile data-[state=open]:lg:animate-drawer-show-desktop data-[state=closed]:animate-drawer-hide-mobile data-[state=closed]:lg:animate-drawer-hide-desktop',
                    isEditing() ? 'relative mt-[-100%] h-full' : 'fixed h-[100dvh]',
                    className
                )}
                data-testid={dataTestId || 'drawer-DrawerContent'}
                {...props}
            >
                <ScrollArea data-testid={dataTestId || 'drawer-ScrollArea'}>{children}</ScrollArea>
            </DrawerPrimitive.Content>
        </DrawerPortal>
    )
})
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex justify-end p-4', className)} {...props}>
        <DrawerPrimitive.Close
            className="w-[1.125rem] h-[1.125rem]"
            onClick={() => document.dispatchEvent(closeDrawerEvent)}
        >
            <DrawerCloseIcon />
            <span className="sr-only">Close</span>
        </DrawerPrimitive.Close>
    </div>
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <DrawerPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        data-testid={dataTestId || 'drawer-DrawerTitle'}
        {...props}
    />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <DrawerPrimitive.Description
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        data-testid={dataTestId || 'drawer-DrawerDescription'}
        {...props}
    />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription
}
